import React, { useState, useEffect } from 'react';
import {
    McButton,
    McCard,
    McBadge,
    McAccordion,
    McAccordionItem,
    McIcon,
    McTag,
    McNotification,
    McSelect,
} from '@maersk-global/mds-react-wrapper';
import { API_DOCS, ApiDocEndpoint } from '../config/docs';
import { API_BASE_URL } from '../config/api';
import { AVAILABLE_TOOLS } from '../components/agents/AddToolDialog';
import ApiPlayground from '../components/refdata/ApiPlayground';
import { TerminalSelect } from '../components/Common/TerminalSelect';
import { useApplicationAccess } from '../hooks/useApplicationAccess';

export default function DeveloperDocsPage() {
    const [copied, setCopied] = useState<string | null>(null);
    const [expandedEndpoints, setExpandedEndpoints] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<'api' | 'agents' | 'refdata'>('api');

    // Reference Data tab state
    const [rdTerminal, setRdTerminal] = useState('');
    const [rdApp, setRdApp] = useState('');
    const [rdTableKey, setRdTableKey] = useState('');
    const [rdSubmitted, setRdSubmitted] = useState(false);
    const { accessibleApplications } = useApplicationAccess();

    // Pre-fill from URL params (e.g. ?tab=refdata&terminal=ESALG&app=compass&tableKey=vessels)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        const t = params.get('terminal');
        const a = params.get('app');
        const k = params.get('tableKey');
        if (tab === 'refdata') setActiveTab('refdata');
        if (t) setRdTerminal(t);
        if (a) setRdApp(a);
        if (k) setRdTableKey(k);
        if (tab === 'refdata' && t && a && k) setRdSubmitted(true);
    }, []);

    const toggleEndpoint = (id: string) => {
        const newExpanded = new Set(expandedEndpoints);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedEndpoints(newExpanded);
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    const generatePostmanCollection = () => {
        const collection = {
            info: {
                name: "APMT CDH Query Service",
                schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
            },
            variable: [
                { key: "baseUrl", value: API_BASE_URL, type: "string" },
                { key: "consumerKey", value: "YOUR_CONSUMER_KEY", type: "string" },
                { key: "jwtToken", value: "YOUR_JWT_TOKEN", type: "string" }
            ],
            item: API_DOCS.map(section => ({
                name: section.title,
                item: section.endpoints.map(endpoint => ({
                    name: endpoint.summary,
                    request: {
                        method: endpoint.method,
                        header: [
                            { key: "Content-Type", value: "application/json" },
                            { key: "Authorization", value: "Bearer {{jwtToken}}" },
                            { key: "Consumer-Key", value: "{{consumerKey}}" }
                        ],
                        body: endpoint.requestBody ? {
                            mode: "raw",
                            raw: JSON.stringify(endpoint.requestBody, null, 2)
                        } : undefined,
                        url: {
                            raw: (endpoint.customBaseUrl || "{{baseUrl}}") + endpoint.path,
                            host: [(endpoint.customBaseUrl || "{{baseUrl}}")],
                            path: endpoint.path.split('/').filter(p => p).map(p => p.startsWith('{') ? `:${p.slice(1, -1)}` : p),
                            variable: endpoint.path.match(/\{([^}]+)\}/g)?.map(v => ({
                                key: v.slice(1, -1),
                                value: ""
                            })) || []
                        },
                        description: endpoint.description
                    }
                }))
            }))
        };
        return collection;
    };

    const handleDownloadCollection = () => {
        const collection = generatePostmanCollection();
        const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'apmt-query-service.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const generateCurlCommand = (endpoint: ApiDocEndpoint) => {
        const baseUrl = endpoint.customBaseUrl || API_BASE_URL;
        const url = `${baseUrl}${endpoint.path
            .replace('{queryId}', 'YOUR_QUERY_ID')
            .replace('{tableName}', 'YOUR_TABLE_NAME')
            .replace('{versionNumber}', 'YOUR_VERSION')}`;
        let cmd = `curl -X ${endpoint.method} "${url}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Consumer-Key: YOUR_CONSUMER_KEY"`;

        if (endpoint.requestBody) {
            cmd += ` \\
  -d '${JSON.stringify(endpoint.requestBody, null, 2)}'`;
        }

        return cmd;
    };

    const getBtnColor = (method: string) => {
        switch (method) {
            case 'GET': return 'var(--mds-color-feedback-success-text)';
            case 'POST': return 'var(--mds-color-brand-primary)';
            case 'DELETE': return 'var(--mds-color-feedback-error-text)';
            default: return 'var(--mds-color-feedback-warning-text)';
        }
    };

    return (
        <>
            <div style={{ marginBottom: '24px' }}>
                <h1 className="mds-headline--medium-bold" style={{ marginBottom: '8px' }}>
                    Developer Documentation
                </h1>
                <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
                    Comprehensive documentation for the Sentinel CDH platform — API Reference, AI Agent Builder, and Tool Catalog.
                </p>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--mds-color-neutral-20)', marginBottom: '32px' }}>
                {[
                    { key: 'api' as const, label: 'API Reference', icon: 'code' },
                    { key: 'agents' as const, label: 'AI Agents Guide', icon: 'wand-magic-sparkles' },
                    { key: 'refdata' as const, label: 'Reference Data', icon: 'database' },
                ].map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '12px 24px', border: 'none', cursor: 'pointer',
                            fontWeight: activeTab === tab.key ? 700 : 400,
                            fontSize: '14px',
                            color: activeTab === tab.key ? 'var(--mds-color-brand-primary)' : 'var(--mds-color-neutral-60)',
                            backgroundColor: 'transparent',
                            borderBottom: activeTab === tab.key ? '3px solid var(--mds-color-brand-primary)' : '3px solid transparent',
                            marginBottom: '-2px',
                            transition: 'all 0.2s',
                        }}
                    >
                        <McIcon icon={tab.icon} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ════════════════ API REFERENCE TAB ════════════════ */}
            {activeTab === 'api' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
                        <McButton appearance="primary" onClick={handleDownloadCollection}>
                            <McIcon icon="tray-arrow-down" slot="icon-start" />
                            Download Collection
                        </McButton>
                    </div>

                    {/* Authentication Section */}
                    <McCard style={{ marginBottom: '32px', borderLeft: '4px solid var(--mds-color-brand-primary)' }}>
                        <div slot="body" style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <McIcon icon="lock" style={{ color: 'var(--mds-color-brand-primary)' }} />
                                <h4 style={{ margin: 0 }}>Authentication</h4>
                            </div>
                            <p className="mds-typography--body-2" style={{ marginBottom: '16px' }}>
                                Secure API access requires two headers:
                            </p>
                            <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
                                <li><code style={{ fontWeight: 600 }}>Authorization</code>: Bearer token (JWT) for user identity and roles.</li>
                                <li><code style={{ fontWeight: 600 }}>Consumer-Key</code>: Application identifier for rate limiting and auditing.</li>
                            </ul>
                            <div style={{
                                padding: '16px',
                                backgroundColor: 'var(--mds-color-background-subtle)',
                                border: '1px solid var(--mds-color-border-subtle)',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '13px'
                            }}>
                                Authorization: Bearer &lt;your-jwt-token&gt;<br />
                                Consumer-Key: &lt;your-app-consumer-key&gt;
                            </div>
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                backgroundColor: 'var(--mds-color-feedback-info-background)',
                                color: 'var(--mds-color-feedback-info-text)',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <McIcon icon="chevron-right" />
                                <span style={{ fontSize: '12px' }}>Contact the CDH Platform Team to request a Consumer Key for your service.</span>
                            </div>
                        </div>
                    </McCard>

                    {/* API Sections */}
                    {API_DOCS.map((section, index) => (
                        <div key={index} style={{ marginBottom: '32px' }}>
                            <h2 className="mds-typography--h2" style={{ marginTop: '32px', marginBottom: '16px' }}>
                                {section.title}
                            </h2>
                            <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '16px' }}>
                                {section.description}
                            </p>

                            <McAccordion>
                                {section.endpoints.map((endpoint, epIndex) => {
                                    const id = `${index}-${epIndex}`;
                                    const isExpanded = expandedEndpoints.has(id);
                                    return (
                                        <McAccordionItem
                                            key={epIndex}
                                            expanded={isExpanded}
                                            accordionitemtoggle={(e: any) => {
                                                const { expanded } = e.detail;
                                                const newExpanded = new Set(expandedEndpoints);
                                                if (expanded) newExpanded.add(id);
                                                else newExpanded.delete(id);
                                                setExpandedEndpoints(newExpanded);
                                            }}
                                        >
                                            <div slot="heading" style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '16px',
                                                width: '100%'
                                            }}>
                                                <McBadge
                                                    label={endpoint.method}
                                                    display="inline"
                                                    style={{
                                                        minWidth: '60px',
                                                        '--mc-badge-text-color': 'white',
                                                        '--mc-badge-background-color': getBtnColor(endpoint.method)
                                                    } as any}
                                                />
                                                <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '14px' }}>
                                                    {endpoint.path}
                                                </span>
                                                <span style={{ marginLeft: 'auto', marginRight: '8px', fontSize: '11px', color: 'var(--mds-color-text-secondary)' }}>
                                                    {endpoint.summary}
                                                </span>
                                            </div>

                                            <div slot="body" style={{
                                                padding: '24px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '24px'
                                            }}>
                                                <div>
                                                    <p className="mds-typography--body-2" style={{ margin: 0 }}>
                                                        {endpoint.description}
                                                    </p>
                                                </div>

                                                {/* CURL Example */}
                                                <div style={{
                                                    backgroundColor: '#0d1117',
                                                    color: '#c9d1d9',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    border: '1px solid #30363d'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '8px 16px',
                                                        backgroundColor: '#161b22',
                                                        borderBottom: '1px solid #30363d'
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <McIcon icon="file" style={{ fontSize: '16px', color: '#8b949e' }} />
                                                            <span style={{ fontSize: '11px', color: '#8b949e', fontWeight: 600 }}>CLI / cURL</span>
                                                        </div>
                                                        <McButton
                                                            variant="plain"
                                                            appearance="neutral"
                                                            onClick={() => handleCopy(generateCurlCommand(endpoint), id)}
                                                            style={{
                                                                '--mc-button-text-color': '#8b949e',
                                                                '--hover-bg': 'rgba(255,255,255,0.05)'
                                                            } as any}
                                                        >
                                                            <McIcon icon="copy" slot="icon-start" />
                                                            {copied === id ? 'Copied!' : 'Copy'}
                                                        </McButton>
                                                    </div>
                                                    <div style={{ padding: '16px', overflowX: 'auto' }}>
                                                        <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '13px', lineHeight: 1.5 }}>
                                                            {generateCurlCommand(endpoint)}
                                                        </pre>
                                                    </div>
                                                </div>

                                                {/* Response Example */}
                                                {endpoint.responseBody && (
                                                    <div>
                                                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--mds-color-text-secondary)', marginBottom: '8px' }}>
                                                            Example Response
                                                        </div>
                                                        <div style={{
                                                            padding: '16px',
                                                            backgroundColor: 'var(--mds-color-background-subtle)',
                                                            border: '1px solid var(--mds-color-border-subtle)',
                                                            borderRadius: '4px',
                                                            maxHeight: '300px',
                                                            overflow: 'auto'
                                                        }}>
                                                            <pre style={{ margin: 0, fontSize: '12px' }}>
                                                                {JSON.stringify(endpoint.responseBody, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </McAccordionItem>
                                    );
                                })}
                            </McAccordion>
                        </div>
                    ))}
                </>
            )}

            {/* ════════════════ AI AGENTS GUIDE TAB ════════════════ */}
            {activeTab === 'agents' && (
                <>
                    {/* Overview Card */}
                    <McCard style={{ marginBottom: '32px', borderLeft: '4px solid #059669' }}>
                        <div slot="body" style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <span style={{ fontSize: '24px' }}>🤖</span>
                                <h4 style={{ margin: 0 }}>AI Agent Builder — Overview</h4>
                            </div>
                            <p className="mds-typography--body-2" style={{ marginBottom: '16px' }}>
                                AI Agents are intelligent, autonomous workflows that combine LLMs with tools to answer questions, execute queries,
                                interact with external systems, and remember context across conversations. Agents are built using a visual drag-and-drop
                                builder and can be triggered from WhatsApp, Microsoft Teams, scheduled cron jobs, or manual API calls.
                            </p>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Visual Builder', desc: 'Drag-and-drop nodes on a canvas', icon: '🎨' },
                                    { label: 'Tools System', desc: 'Pluggable tools for AI to use', icon: '🛠️' },
                                    { label: 'Multi-Channel', desc: 'WhatsApp, Teams, Web, API', icon: '📡' },
                                    { label: 'Memory', desc: 'In-Memory or Postgres persistence', icon: '🧠' },
                                ].map((item) => (
                                    <div key={item.label} style={{
                                        flex: '1 1 200px', padding: '16px', borderRadius: '8px',
                                        border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
                                    }}>
                                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{item.label}</div>
                                        <div style={{ fontSize: '13px', color: '#6B7280' }}>{item.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </McCard>

                    {/* How to Build an Agent */}
                    <h2 className="mds-typography--h2" style={{ marginTop: '32px', marginBottom: '16px' }}>
                        How to Build an Agent
                    </h2>
                    <McAccordion>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontWeight: 600 }}>Step 1: Create a new agent</div>
                            <div slot="body" style={{ padding: '1rem' }}>
                                <p>Navigate to <strong>AI Agents</strong> from the main menu and click <strong>"New Agent"</strong>.</p>
                                <p>You'll land on the Agent Builder canvas with a left panel of draggable components.</p>
                            </div>
                        </McAccordionItem>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontWeight: 600 }}>Step 2: Add a Trigger</div>
                            <div slot="body" style={{ padding: '1rem' }}>
                                <p>Drag a <strong>Trigger</strong> node onto the canvas. Choose a trigger type:</p>
                                <ul>
                                    <li><strong>📱 WhatsApp</strong> — agent listens for incoming WhatsApp messages</li>
                                    <li><strong>💬 Microsoft Teams</strong> — agent receives Teams bot messages</li>
                                    <li><strong>⏰ Scheduled (Cron)</strong> — agent runs on a cron schedule</li>
                                    <li><strong>👆 Manual</strong> — agent is triggered via REST API</li>
                                </ul>
                            </div>
                        </McAccordionItem>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontWeight: 600 }}>Step 3: Add an AI Action node</div>
                            <div slot="body" style={{ padding: '1rem' }}>
                                <p>Drag an <strong>AI Action</strong> node and connect it to the trigger. Configure:</p>
                                <ul>
                                    <li><strong>System Prompt</strong> — instructions that tell the LLM who it is and how to behave</li>
                                    <li><strong>Model</strong> — GPT-4o, GPT-4o Mini, or GPT-3.5 Turbo</li>
                                    <li><strong>Temperature</strong> — 0.0 (deterministic) to 1.0 (creative)</li>
                                </ul>
                                <p>Or drag a <strong>Recipe</strong> (pre-configured prompt template) from the top of the palette.</p>
                            </div>
                        </McAccordionItem>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontWeight: 600 }}>Step 4: Assign Tools to the AI Action</div>
                            <div slot="body" style={{ padding: '1rem' }}>
                                <p>Select the AI Action node, then click <strong>"Add Tool"</strong> in the right panel. Tools give the AI the ability to take actions:</p>
                                <ul>
                                    <li>Query terminals, search/execute saved queries</li>
                                    <li>Call external REST APIs</li>
                                    <li>Read/write GitHub repositories</li>
                                    <li>Save and retrieve memory (user preferences, facts)</li>
                                </ul>
                                <p>Each tool that requires configuration (like GitHub or Memory) will show config fields after being added.</p>
                            </div>
                        </McAccordionItem>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontWeight: 600 }}>Step 5: Save, Test, and Deploy</div>
                            <div slot="body" style={{ padding: '1rem' }}>
                                <ul>
                                    <li><strong>Save</strong> — persists the workflow as a draft</li>
                                    <li><strong>Test</strong> — sets status to TESTING and lets you try it in the Test Chat panel</li>
                                    <li><strong>Deploy</strong> — sets status to ACTIVE, making the agent live for all channels</li>
                                </ul>
                                <p>Use <strong>Activity Logs</strong> to monitor execution history, tool calls, and errors.</p>
                            </div>
                        </McAccordionItem>
                    </McAccordion>

                    {/* Tool Catalog */}
                    <h2 className="mds-typography--h2" style={{ marginTop: '32px', marginBottom: '16px' }}>
                        Tool Catalog
                    </h2>
                    <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '16px' }}>
                        Tools are capabilities that the AI can use autonomously during execution. Below is the full catalog of available and planned tools.
                    </p>

                    {/* Group tools by category */}
                    {(() => {
                        const categories = Array.from(new Set(AVAILABLE_TOOLS.map(t => t.category)));
                        return categories.map(cat => {
                            const tools = AVAILABLE_TOOLS.filter(t => t.category === cat);
                            return (
                                <div key={cat} style={{ marginBottom: '24px' }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                                        {cat}
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                                        {tools.map(tool => (
                                            <div key={tool.id} style={{
                                                padding: '16px', borderRadius: '8px',
                                                border: '1px solid #E5E7EB',
                                                backgroundColor: tool.status === 'coming_soon' ? '#FAFAFA' : 'white',
                                                opacity: tool.status === 'coming_soon' ? 0.7 : 1,
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                    <McIcon icon={tool.icon} style={{ color: '#6B7280' }} />
                                                    <strong>{tool.label}</strong>
                                                    {tool.status === 'coming_soon' && (
                                                        <span style={{
                                                            fontSize: '10px', fontWeight: 600, padding: '2px 8px',
                                                            borderRadius: '10px', backgroundColor: '#FEF3C7', color: '#92400E',
                                                        }}>COMING SOON</span>
                                                    )}
                                                    {tool.status === 'beta' && (
                                                        <span style={{
                                                            fontSize: '10px', fontWeight: 600, padding: '2px 8px',
                                                            borderRadius: '10px', backgroundColor: '#DBEAFE', color: '#1E40AF',
                                                        }}>BETA</span>
                                                    )}
                                                    {tool.status === 'available' && (
                                                        <span style={{
                                                            fontSize: '10px', fontWeight: 600, padding: '2px 8px',
                                                            borderRadius: '10px', backgroundColor: '#D1FAE5', color: '#065F46',
                                                        }}>AVAILABLE</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#6B7280' }}>{tool.description}</div>
                                                {tool.requiresConfig && (
                                                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF' }}>
                                                        🔧 Requires configuration
                                                        {tool.configFields && tool.configFields.length > 0 && (
                                                            <span> ({tool.configFields.map(f => f.label).join(', ')})</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        });
                    })()}

                    {/* Agent Execution API */}
                    <h2 className="mds-typography--h2" style={{ marginTop: '32px', marginBottom: '16px' }}>
                        Agent Execution API
                    </h2>
                    <p className="mds-typography--body-2" style={{ color: 'var(--mds-color-text-secondary)', marginBottom: '16px' }}>
                        All agent endpoints live on the <strong>Agent Service</strong> (<code>http://localhost:8086/apmt-cdh-agent-service</code>).
                        Authentication: <code>Authorization: Bearer &lt;JWT&gt;</code>.
                    </p>

                    {/* Execute Agent */}
                    <McCard style={{ marginBottom: '16px', borderLeft: '4px solid var(--mds-color-brand-primary)' }}>
                        <div slot="body" style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <McBadge label="POST" display="inline" style={{ '--mc-badge-text-color': 'white', '--mc-badge-background-color': 'var(--mds-color-brand-primary)' } as any} />
                                <code style={{ fontWeight: 600, fontSize: '14px' }}>/agents/{'{agentId}'}/execute</code>
                                <span style={{ fontSize: '12px', color: '#6B7280' }}>Execute Agent Workflow</span>
                            </div>
                            <p className="mds-typography--body-2" style={{ marginBottom: '12px' }}>
                                Executes a specific intelligent agent and returns its text response. The agent autonomously selects and calls its assigned tools (queries, APIs, SharePoint, Jira, etc.).
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                <div>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Request Body</div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#0d1117', color: '#c9d1d9', padding: '12px', borderRadius: '6px' }}>
                                        <pre style={{ margin: 0 }}>{`{
  "message": "string (required)",
  "channel": "web_ui | whatsapp | teams",
  "userId": "user.name@maersk.com",
  "replyTo": "optional-message-id"
}`}</pre>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Response (200)</div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#F9FAFB', padding: '12px', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                                        <pre style={{ margin: 0 }}>{`{
  "executionId": "uuid",
  "agentId": "uuid",
  "response": "The yard utilization...",
  "status": "SUCCESS",
  "executionDurationMs": 4250,
  "toolCallCount": 2,
  "executedAt": "2026-03-09T18:00:00Z"
}`}</pre>
                                    </div>
                                </div>
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#0d1117', color: '#c9d1d9', padding: '12px', borderRadius: '6px', overflowX: 'auto' }}>
                                <pre style={{ margin: 0 }}>{`curl -X POST 'http://localhost:8086/apmt-cdh-agent-service/agents/{agent-uuid}/execute' \\
  -H 'Authorization: Bearer <token>' \\
  -H 'Content-Type: application/json' \\
  -d '{ "message": "What is yard utilization at ESALG?", "channel": "web_ui", "userId": "user@maersk.com" }'`}</pre>
                            </div>
                        </div>
                    </McCard>

                    {/* CRUD Endpoints */}
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginTop: '24px', marginBottom: '12px' }}>
                        Agent Management (CRUD)
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        {[
                            { method: 'GET', path: '/agents', desc: 'List all agents. Optional query param: status (DRAFT, TESTING, ACTIVE, PAUSED, ARCHIVED)' },
                            { method: 'POST', path: '/agents', desc: 'Create a new agent. Body: CreateAgentRequest { name, description, triggerType, workflowDefinition, systemPrompt }' },
                            { method: 'GET', path: '/agents/{agentId}', desc: 'Get a single agent by UUID' },
                            { method: 'PUT', path: '/agents/{agentId}', desc: 'Update an agent. Body: UpdateAgentRequest { name, description, workflowDefinition, systemPrompt, ... }' },
                            { method: 'DELETE', path: '/agents/{agentId}', desc: 'Delete an agent (returns 204 No Content)' },
                        ].map((ep, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: 'white' }}>
                                <McBadge label={ep.method} display="inline" style={{ '--mc-badge-text-color': 'white', '--mc-badge-background-color': ep.method === 'GET' ? 'var(--mds-color-feedback-success-text)' : ep.method === 'POST' ? 'var(--mds-color-brand-primary)' : ep.method === 'DELETE' ? 'var(--mds-color-feedback-error-text)' : 'var(--mds-color-feedback-warning-text)', minWidth: '60px' } as any} />
                                <code style={{ fontWeight: 600, fontSize: '13px', minWidth: '200px' }}>{ep.path}</code>
                                <span style={{ fontSize: '13px', color: '#6B7280' }}>{ep.desc}</span>
                            </div>
                        ))}
                    </div>

                    {/* Lifecycle Endpoints */}
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                        Agent Lifecycle
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        {[
                            { method: 'POST', path: '/agents/{agentId}/deploy', desc: 'Deploy agent → status becomes ACTIVE. Agent is live on all configured channels.' },
                            { method: 'POST', path: '/agents/{agentId}/test', desc: 'Enter sandbox mode → status becomes TESTING. Use the Test Chat panel to try it.' },
                            { method: 'POST', path: '/agents/{agentId}/pause', desc: 'Pause a deployed agent → status becomes PAUSED. Channels stop receiving responses.' },
                        ].map((ep, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: 'white' }}>
                                <McBadge label={ep.method} display="inline" style={{ '--mc-badge-text-color': 'white', '--mc-badge-background-color': 'var(--mds-color-brand-primary)', minWidth: '60px' } as any} />
                                <code style={{ fontWeight: 600, fontSize: '13px', minWidth: '260px' }}>{ep.path}</code>
                                <span style={{ fontSize: '13px', color: '#6B7280' }}>{ep.desc}</span>
                            </div>
                        ))}
                    </div>

                    {/* Execution Logs */}
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                        Execution Logs
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: 'white' }}>
                            <McBadge label="GET" display="inline" style={{ '--mc-badge-text-color': 'white', '--mc-badge-background-color': 'var(--mds-color-feedback-success-text)', minWidth: '60px' } as any} />
                            <code style={{ fontWeight: 600, fontSize: '13px', minWidth: '260px' }}>/agents/{'{agentId}'}/logs</code>
                            <span style={{ fontSize: '13px', color: '#6B7280' }}>Paginated audit log of tool-call executions. Query params: page (default 0), size (default 20).</span>
                        </div>
                    </div>
                    <McCard style={{ marginBottom: '16px' }}>
                        <div slot="body" style={{ padding: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Log Entry Shape (ToolCallLogResponse)</div>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#F9FAFB', padding: '12px', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                                <pre style={{ margin: 0 }}>{`{
  "logId": "uuid",
  "agentId": "uuid",
  "toolName": "executeQuery",
  "inputData": "{ queryId: 'q1', terminalId: 'ESALG' }",
  "outputData": "Yard utilization is 85.2%.",
  "status": "SUCCESS | ERROR",
  "durationMs": 1250,
  "errorMessage": null,
  "channel": "whatsapp",
  "userId": "user@maersk.com",
  "executedAt": "2026-03-09T18:00:00Z"
}`}</pre>
                            </div>
                        </div>
                    </McCard>

                    {/* Channel Webhooks */}
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                        Channel Webhooks
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                        {[
                            { method: 'POST', path: '/channels/whatsapp/webhook', desc: 'Twilio WhatsApp inbound webhook (form-urlencoded: From, Body, MediaUrl0). No auth required.' },
                            { method: 'POST', path: '/channels/teams/webhook', desc: 'Microsoft Teams Bot Framework activity. Body: { type, text, from, conversation, serviceUrl }. No auth required.' },
                        ].map((ep, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: 'white' }}>
                                <McBadge label={ep.method} display="inline" style={{ '--mc-badge-text-color': 'white', '--mc-badge-background-color': 'var(--mds-color-brand-primary)', minWidth: '60px' } as any} />
                                <code style={{ fontWeight: 600, fontSize: '13px', minWidth: '280px' }}>{ep.path}</code>
                                <span style={{ fontSize: '13px', color: '#6B7280' }}>{ep.desc}</span>
                            </div>
                        ))}
                    </div>

                </>
            )}

            {/* ════════════════ REFERENCE DATA TAB ════════════════ */}
            {activeTab === 'refdata' && (
                <>
                    <p style={{ fontSize: '0.875rem', color: '#4B5563', marginBottom: '1.25rem' }}>
                        Explore and try the Reference Data Service API. Select a terminal, application, and table key to generate live code snippets and run requests.
                    </p>

                    {/* Context inputs */}
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: '0.75rem',
                        padding: '1rem',
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        marginBottom: '1.25rem',
                    }}>
                        {/* Terminal — reuse the same access-controlled dropdown */}
                        <div style={{ flex: '1 1 200px' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Terminal</label>
                            <TerminalSelect
                                value={rdTerminal}
                                onChange={(v: string) => { setRdTerminal(v); setRdSubmitted(false); }}
                                placeholder="Select terminal…"
                            />
                        </div>

                        {/* Application — access-controlled dropdown from user roles */}
                        <div style={{ flex: '1 1 200px' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Application</label>
                            <McSelect
                                placeholder="Select application…"
                                value={rdApp}
                                data={accessibleApplications.map(a => ({
                                    label: a.name || a.applicationId,
                                    value: (a.name || a.applicationId).toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                                }))}
                                optionselected={(e: any) => {
                                    const val = e.detail?.value || e.detail || '';
                                    setRdApp(val);
                                    setRdSubmitted(false);
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>

                        {/* Table Key — schema-specific, stays as free text */}
                        <div style={{ flex: '1 1 180px' }}>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' }}>Table Key</label>
                            <input
                                type="text"
                                value={rdTableKey}
                                onChange={e => { setRdTableKey(e.target.value); setRdSubmitted(false); }}
                                placeholder="e.g. vessel_schedule"
                                style={{
                                    width: '100%',
                                    padding: '0.4rem 0.6rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '0.875rem',
                                    color: '#111827',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>

                        <button
                            onClick={() => setRdSubmitted(true)}
                            disabled={!rdTerminal || !rdApp || !rdTableKey.trim()}
                            style={{
                                padding: '0.45rem 1.25rem',
                                backgroundColor: '#0a6e82',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: (!rdTerminal || !rdApp || !rdTableKey.trim()) ? 'not-allowed' : 'pointer',
                                opacity: (!rdTerminal || !rdApp || !rdTableKey.trim()) ? 0.6 : 1,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Load API Docs
                        </button>
                    </div>

                    {rdSubmitted && rdTerminal && rdApp && rdTableKey ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                <button
                                    onClick={() => {
                                        const params = new URLSearchParams({ terminal: rdTerminal.trim(), app: rdApp.trim() });
                                        window.location.href = `/ref-data?${params.toString()}`;
                                    }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        padding: '0.4rem 0.9rem',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    ← View in Reference Data
                                </button>
                                <button
                                    onClick={() => {
                                        const base = `/api/v1`;
                                        const t = rdTerminal.trim();
                                        const a = rdApp.trim();
                                        const k = rdTableKey.trim();
                                        const schemaBase = `${base}/${t}/${a}/schemas`;
                                        const rowBase = `${base}/${t}/${a}/schemas/${k}/rows`;
                                        const exCols = [
                                            { key: 'code', displayName: 'Code', type: 'STRING', required: true },
                                            { key: 'name', displayName: 'Name', type: 'STRING', required: true },
                                            { key: 'is_active', displayName: 'Active', type: 'BOOLEAN' },
                                        ];
                                        const collection = {
                                            info: {
                                                name: `Reference Data — ${t} / ${a} / ${k}`,
                                                schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
                                            },
                                            variable: [
                                                { key: 'baseUrl', value: 'http://localhost:8087', type: 'string' },
                                                { key: 'token', value: '<your_token>', type: 'string' },
                                            ],
                                            item: [
                                                {
                                                    name: 'Schemas',
                                                    item: [
                                                        { name: 'List schemas', request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }], url: { raw: `{{baseUrl}}${schemaBase}` } } },
                                                        { name: 'Create schema', request: { method: 'POST', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }, { key: 'Content-Type', value: 'application/json' }], body: { mode: 'raw', raw: JSON.stringify({ tableKey: k, displayName: 'Example Table', description: '', columns: exCols, containsPii: false }, null, 2) }, url: { raw: `{{baseUrl}}${schemaBase}` } } },
                                                        { name: 'Update schema', request: { method: 'PATCH', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }, { key: 'Content-Type', value: 'application/json' }], body: { mode: 'raw', raw: JSON.stringify({ displayName: 'Example Table', description: '', columns: exCols, containsPii: false }, null, 2) }, url: { raw: `{{baseUrl}}${schemaBase}/${k}` } } },
                                                        { name: 'Schema history', request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }], url: { raw: `{{baseUrl}}${schemaBase}/${k}/history` } } },
                                                    ],
                                                },
                                                {
                                                    name: 'Rows',
                                                    item: [
                                                        { name: 'List rows', request: { method: 'GET', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }], url: { raw: `{{baseUrl}}${rowBase}` } } },
                                                        { name: 'Create row', request: { method: 'POST', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }, { key: 'Content-Type', value: 'application/json' }], body: { mode: 'raw', raw: JSON.stringify({ data: { code: 'EXAMPLE', name: 'Example Row', is_active: true } }, null, 2) }, url: { raw: `{{baseUrl}}${rowBase}` } } },
                                                        { name: 'Update row', request: { method: 'PUT', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }, { key: 'Content-Type', value: 'application/json' }], body: { mode: 'raw', raw: JSON.stringify({ data: { code: 'EXAMPLE', name: 'Updated Name', is_active: false } }, null, 2) }, url: { raw: `{{baseUrl}}${rowBase}/{{rowId}}` } } },
                                                        { name: 'Delete row', request: { method: 'DELETE', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }], url: { raw: `{{baseUrl}}${rowBase}/{{rowId}}` } } },
                                                        { name: 'Bulk replace', request: { method: 'POST', header: [{ key: 'Authorization', value: 'Bearer {{token}}' }, { key: 'Content-Type', value: 'application/json' }], body: { mode: 'raw', raw: JSON.stringify({ rows: [{ data: { code: 'ROW1', name: 'First Row', is_active: true } }] }, null, 2) }, url: { raw: `{{baseUrl}}${rowBase}/bulk` } } },
                                                    ],
                                                },
                                            ],
                                        };
                                        const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
                                        const url = URL.createObjectURL(blob);
                                        const anchor = document.createElement('a');
                                        anchor.href = url;
                                        anchor.download = `refdata-${t}-${a}-${k}.postman_collection.json`;
                                        document.body.appendChild(anchor);
                                        anchor.click();
                                        document.body.removeChild(anchor);
                                        URL.revokeObjectURL(url);
                                    }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        padding: '0.4rem 0.9rem',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                    }}
                                >
                                    ⬇ Download Postman Collection
                                </button>
                            </div>
                            <ApiPlayground terminal={rdTerminal.trim()} app={rdApp.trim()} tableKey={rdTableKey.trim()} />
                        </>
                    ) : (
                        <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#9ca3af',
                            border: '1px dashed #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                        }}>
                            Enter a terminal, app, and table key above then click <strong>Load API Docs</strong> to generate the endpoint reference.
                        </div>
                    )}
                </>
            )}
        </>
    );
}
